import React, { useState, useMemo } from "react";
import { MdAddBox } from "react-icons/md";
import { IoIosRefreshCircle } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { SlOptionsVertical } from "react-icons/sl";
import ModalCreateQuestion from "components/QuestionsAnswers/Questions/ModalCreateQuestions";
import { getQuestionList } from "actions/Catalogs";
import { format, parseISO } from "date-fns";
import { setQuestionById } from "redux/catalog.slice";
import { notify } from "utils";
import Loader from "components/UI/Loader";

const columnHelper = createColumnHelper();
const statusMap = {
  true: 'Activo',
  false: 'Inactivo',
};

const getColumns = (onViewDetails) => [
  columnHelper.accessor("id", { header: "Id Pregunta" }),
  columnHelper.accessor("description", { header: "Nombre" }),
  columnHelper.accessor("createdAt", {
    header: "Fecha Alta",
    cell: ({ getValue }) => {
      const value = getValue();
      if (value) return format(parseISO(value), "dd/MM/yyyy HH:mm");
      return value;
    },
  }),
  columnHelper.accessor("active", {
    header: "Estatus",
    cell: ({ getValue }) => (
      <button className={` ${getValue() === true ? 'bg-teal-200' : 'bg-[#fdca3b]' } text-black text-xs font-bold rounded-full px-4 py-1 shadow-inner border`}>
        {statusMap[getValue()]}
      </button>
    ),
  }),
  columnHelper.display({
    id: "actions",
    cell: ({ row }) => (
      <div onClick={() => onViewDetails(row)} className="flex justify-center">
        <SlOptionsVertical className="rotate-180 text-xl cursor-pointer hover:text-yellow-800" />
      </div>
    ),
  }),
];

const Questions = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filterText, setFilterText] = useState("");
  const { questionsList } = useSelector((state) => state.catalog);
  const { currentProject } = useSelector((state) => state.auth);
  const [isLoading, setIsLoading] = useState(false);

  //Funcion para obtener las preguntas
  React.useEffect(() => {
    if (currentProject && currentProject?.id) {
      dispatch(getQuestionList());
    }
  }, [dispatch, currentProject]);

  const onViewDetails = ({ original }) => {
    dispatch(setQuestionById(original));
    setIsOpen(true);
  }

  const onRefresh = async () => {
    try {
      setIsLoading(true);
      await dispatch(getQuestionList());
    } catch (error) {
      console.error("Error al guardar la pregunta:", error);
      notify("error", "Hubo un problema al guardar", 2000);
    } finally {
      setIsLoading(false);
    }
  }

  const filteredQuestions = useMemo(() => {
    if (!filterText) return questionsList || [];
    return (questionsList || []).filter(q =>
      (q.name || q.description || "").toLowerCase().includes(filterText.toLowerCase())
    );
  }, [questionsList, filterText]);

  const table = useReactTable({
    data: filteredQuestions || [],
    columns: getColumns(onViewDetails),
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
      setPageIndex(next.pageIndex);
      setPageSize(next.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });


  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-screen">
      <div className="bg-[#000000] rounded-t-lg h-[59px] w-full flex items-center justify-between px-4">
        <text className="text-[#ffffff] text-[16px] font-[Gravity] font-bold">
          Preguntas
        </text>
        <div className="flex gap-2 text-white text-4xl">
          <MdAddBox className="cursor-pointer" onClick={() => setIsOpen(true)} />
          <IoIosRefreshCircle className="cursor-pointer" onClick={onRefresh} />
        </div>
      </div>
      <div className={`flex flex-row items-start w-full h-3/4 overflow-x-scroll overflow-y-scroll shadow-scroll-x`}>
        <div className="w-full bg-white px-6 pt-4 pb-6 rounded-b-xl shadow-xl">
          <div className="overflow-x-auto">
            <div className="w-full flex justify-start mt-4">
              <input
                type="text"
                placeholder="Buscar"
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                className="border border-gray-800 rounded px-3 py-2 w-72"
              />
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-white border-b-2 border-gray-400">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-4 py-5 text-left font-semibold text-black whitespace-nowrap"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="border-b-2 border-gray-300 hover:bg-gray-50">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-4 py-2 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Paginación con estilo personalizado */}
          <div className="flex flex-row items-center justify-center mt-4">
            <button
              className="w-[20px] h-[20px]"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <img
                src={`${process.env.PUBLIC_URL}/icons/btns/btn_arrow_prev.svg`}
                width={50}
                alt="Anterior"
              />
            </button>

            {table.getCanPreviousPage() && (
              <button
                className="m-2 px-2 py-1 cursor-pointer"
                onClick={() => table.setPageIndex(table.getState().pagination.pageIndex - 1)}
              >
                <span className="font-bold font-[Gravity] text-[12px]">
                  {table.getState().pagination.pageIndex}
                </span>
              </button>
            )}

            <button
              className="bg-[#BCBEC1] m-2 rounded px-2 py-1 cursor-pointer"
              onClick={() => table.setPageIndex(table.getState().pagination.pageIndex)}
            >
              <span className="font-bold font-[Gravity] text-[12px]">
                {table.getState().pagination.pageIndex + 1}
              </span>
            </button>

            {table.getCanNextPage() && (
              <button
                className="m-2 px-2 py-1 cursor-pointer"
                onClick={() => table.setPageIndex(table.getState().pagination.pageIndex + 1)}
              >
                <span className="font-bold font-[Gravity] text-[12px]">
                  {table.getState().pagination.pageIndex + 2}
                </span>
              </button>
            )}

            <button
              className="w-[20px] h-[20px]"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <img
                src={`${process.env.PUBLIC_URL}/icons/btns/btn_arrow_next.svg`}
                width={50}
                alt="Siguiente"
              />
            </button>
          </div>
        </div>
      </div>
      <ModalCreateQuestion isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </div>
  )
}

export default Questions